'use client';

import React, { useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { MOCK_FEED_RETURNS, FeedReturn } from './feedData';

const CONDITION_STYLES: Record<string, string> = {
  Good: 'bg-emerald-100 text-emerald-700',
  Damaged: 'bg-amber-100 text-amber-700',
  Contaminated: 'bg-red-100 text-red-700',
};

export default function FeedReturnsTab() {
  const [returns, setReturns] = useState<FeedReturn[]>(MOCK_FEED_RETURNS);
  const [search, setSearch] = useState('');
  const [filterCondition, setFilterCondition] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ date: '2026-09-05', issuanceNumber: '', feedTypeName: '', flockName: '', house: '', farm: 'Idudi Poultry Farm', returnedQtyKg: '', reason: '', condition: 'Good\' as \'Good\' | \'Damaged\' | \'Contaminated', returnedBy: '', receivedBy: '', notes: '' });

  const filtered = returns.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.returnNumber.toLowerCase().includes(q) || r.flockName.toLowerCase().includes(q) || r.feedTypeName.toLowerCase().includes(q);
    const matchCond = filterCondition === 'all' || r.condition === filterCondition;
    return matchSearch && matchCond;
  });

  function handleAdd() {
    if (!form.feedTypeName || !form.flockName || !form.returnedQtyKg) return;
    const newReturn: FeedReturn = {
      id: `fr-${Date.now()}`, returnNumber: `FR-2026-${String(returns.length + 46).padStart(4, '0')}`,
      date: form.date, issuanceId: '', issuanceNumber: form.issuanceNumber,
      feedTypeName: form.feedTypeName, flockName: form.flockName, house: form.house, farm: form.farm,
      returnedQtyKg: Number(form.returnedQtyKg), reason: form.reason, condition: form.condition,
      returnedBy: form.returnedBy, receivedBy: form.receivedBy, notes: form.notes,
    };
    setReturns(prev => [newReturn, ...prev]);
    setShowModal(false);
  }

  const totalReturned = filtered.reduce((s, r) => s + r.returnedQtyKg, 0);
  const goodCount = filtered.filter(r => r.condition === 'Good').length;
  const contaminatedCount = filtered.filter(r => r.condition === 'Contaminated').length;

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Total Returned (kg)', value: totalReturned.toFixed(1), color: 'text-purple-600' },
          { label: 'Good Condition', value: goodCount, color: 'text-emerald-600' },
          { label: 'Contaminated', value: contaminatedCount, color: 'text-red-600' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search returns…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterCondition} onChange={e => setFilterCondition(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Conditions</option>
          <option value="Good">Good</option>
          <option value="Damaged">Damaged</option>
          <option value="Contaminated">Contaminated</option>
        </select>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg whitespace-nowrap" style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={14} /> Record Return
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Return #','Date','Issuance Ref','Feed Type','Flock','House / Farm','Returned (kg)','Reason','Condition','Returned By','Received By'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" style={{ backgroundColor: 'var(--card-bg)' }}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-purple-600">{r.returnNumber}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.date}</td>
                <td className="px-4 py-3 font-mono text-xs text-indigo-500">{r.issuanceNumber}</td>
                <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">{r.feedTypeName}</td>
                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.flockName}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  <div className="text-xs">{r.house}</div>
                  <div className="text-xs text-gray-400">{r.farm}</div>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-purple-600">{r.returnedQtyKg.toFixed(1)}</td>
                <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate" title={r.reason}>{r.reason}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONDITION_STYLES[r.condition]}`}>{r.condition}</span></td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.returnedBy}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{r.receivedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No return records found</div>}
      </div>

      {/* Record Return Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-2xl p-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Record Feed Return</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Date', key: 'date', type: 'date' },
                { label: 'Issuance Reference', key: 'issuanceNumber', type: 'text' },
                { label: 'Feed Type', key: 'feedTypeName', type: 'text' },
                { label: 'Flock Name', key: 'flockName', type: 'text' },
                { label: 'House', key: 'house', type: 'text' },
                { label: 'Farm', key: 'farm', type: 'text' },
                { label: 'Returned Qty (kg)', key: 'returnedQtyKg', type: 'number' },
                { label: 'Returned By', key: 'returnedBy', type: 'text' },
                { label: 'Received By', key: 'receivedBy', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                  <input type={f.type} value={(form as any)[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--surface)' }} />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Condition</label>
                <select value={form.condition} onChange={e => setForm(prev => ({ ...prev, condition: e.target.value as any }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--surface)' }}>
                  <option value="Good">Good</option>
                  <option value="Damaged">Damaged</option>
                  <option value="Contaminated">Contaminated</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason for Return</label>
                <textarea value={form.reason} onChange={e => setForm(prev => ({ ...prev, reason: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none" style={{ backgroundColor: 'var(--surface)' }} />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: 'var(--primary)' }}>Record Return</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
